import {
  doc,
  query,
  onSnapshot,
  collection,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from 'firebase/firestore';
import React, { useEffect, useRef, useState } from 'react';

import { db } from '../../firebase';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeIsUserSelected,
  changeUser,
  clearReplyMessage,
  resetChatUser,
  updateUserStatus,
} from '../../reducers/enqChatSlice';
import { formatMessage } from '../../utils/chatUtils';
import CustomerChatInfo from './CustomerChatInfo';
import { isTextOrNumber, normalizePhone } from '../../utils/commonUtils';
import { API_POST_UNREADMESSAGE } from '../../api/chat.service';
import { FIREBASE_DB_NAME, inOrderTO, queryFieldNames, searchFieldKeys } from './chats.constant.js';
const getQueryParams = (
  searchText,
  ticketStatusFilter,
  tagFilter,
  dateTimeRangeFilter,
  limitValue,
  botNumber,
  agentChatStatus,
  allotedAgent,
  selectedAgent,
  lastMessage = null,
) => {
  const searchField = isTextOrNumber(searchText)
    ? searchFieldKeys.PHONE
    : searchFieldKeys.BUSINESS_NAME;
  const searchValue = isTextOrNumber(searchText) ? `${searchText}` : searchText;
  const hasTicketStatusFilter = ticketStatusFilter && ticketStatusFilter.length !== 0;
  const hasAgentSearchItem = searchText && searchText.length !== 0;
  let requests = [];
  requests.push(collection(db, FIREBASE_DB_NAME));
  requests.push(where(queryFieldNames.BOT_NUMBER, '==', botNumber));
  if (agentChatStatus === 'ai_chat') {
    requests.push(where(queryFieldNames.AI_CHAT, '==', true));
  } else if (agentChatStatus !== 'all') {
    requests.push(where(queryFieldNames.AGENT_CHAT_STATUS, '==', agentChatStatus));
  }
  if (allotedAgent.role === 'support') {
    let IDs = [allotedAgent?.agentId, -1];
    requests.push(where(queryFieldNames.AGENT_ID, 'in', IDs));
  }
  if (allotedAgent.role !== 'support' && selectedAgent) {
    requests.push(where(queryFieldNames.AGENT_ID, '==', selectedAgent));
  }
  if (hasTicketStatusFilter) {
    requests.push(where(queryFieldNames.TICKET_STATUS, '==', ticketStatusFilter));
  }
  if (tagFilter) {
    requests.push(where('tag', 'array-contains', tagFilter));
  }
  if (dateTimeRangeFilter?.start) {
    requests.push(where(queryFieldNames.LAST_UPDATED_AT, '>=', dateTimeRangeFilter.start));
  }
  if (dateTimeRangeFilter?.end) {
    requests.push(where(queryFieldNames.LAST_UPDATED_AT, '<=', dateTimeRangeFilter.end));
  }
  if (hasAgentSearchItem) {
    requests.push(where(searchField, '>=', searchValue));
    requests.push(where(searchField, '<=', searchValue + '\uf8ff'));
    requests.push(orderBy(searchField));
  }
  requests.push(orderBy(queryFieldNames.LAST_UPDATED_AT, inOrderTO.DESC));
  if (lastMessage) {
    requests.push(startAfter(lastMessage));
  }
  requests.push(limit(limitValue));

  return query(...requests);
};

const loadingSpinner = () => (
  <div className="h-full flex flex-column align-items-center justify-content-center">
    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }}></i>
  </div>
);

const Chats = () => {
  const customerContainerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [chats, setChats] = useState([]);
  const [lastMessage, setLastMessage] = useState(null);

  const {
    allotedAgent,
    agentSearchItem,
    user,
    ticketStatusFilter,
    selectedAgent,
    agentChatStatus,
    tagFilter,
    dateTimeRangeFilter,
  } = useSelector((state) => state.enqChats);

  const { company } = useSelector((state) => state.company);
  const dispatch = useDispatch();

  const botNumber = company?.phone;
  const handleSelect = (chat) => {
    API_POST_UNREADMESSAGE({ customerMobile: user?.phone });
    dispatch(changeIsUserSelected(true));
    dispatch(changeUser(chat));
    dispatch(clearReplyMessage());
    agentChatStatus === 'all' &&
      chat?.unreadMessages &&
      API_POST_UNREADMESSAGE({ customerMobile: chat?.phone });
  };

  useEffect(() => {
    if (!botNumber || !allotedAgent.role || !allotedAgent.mob) return;
    let unsubscribe;
    const getChats = async () => {
      let q = query(
        getQueryParams(
          agentSearchItem,
          ticketStatusFilter,
          tagFilter,
          dateTimeRangeFilter,
          25,
          botNumber,
          agentChatStatus,
          allotedAgent,
          selectedAgent,
          null,
        ),
      );
      setLoading(true);
      try {
        unsubscribe = onSnapshot(q, (querySnapshot) => {
          setLoading(false);
          const groups = [];
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            let conversationBetween = doc.id.split('-');

            let bot = conversationBetween[0];
            if (bot == allotedAgent.mob) {
              let lastMessage = '';
              if (data?.lastMessage) {
                try {
                  lastMessage = formatMessage(
                    JSON.parse(data?.lastMessage),
                    data.messageType,
                    true,
                  );
                } catch (error) {
                  console.log(error);
                }
              }

              const conversation = {
                ...data,
                lastMessage: lastMessage,
                enqStartedAt: data?.enquiryStartTime,
              };
              groups.push(conversation);
            }
          });
          setChats(groups);
          if (groups.length > 0) {
            setLastMessage(querySnapshot.docs[querySnapshot.docs.length - 1]);
          }
        });
      } catch (error) {
        console.log('error', error);
      }
    };

    getChats();
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [
    allotedAgent,
    agentSearchItem,
    ticketStatusFilter,
    tagFilter,
    dateTimeRangeFilter,
    selectedAgent,
    agentChatStatus,
  ]);

  useEffect(() => {
    chats?.forEach((chat) => {
      if (
        user?.phone == chat?.phone &&
        (user.ticketStatus !== chat?.ticketStatus ||
          user.ticketId !== chat?.ticketId ||
          user.enqStartedAt !== chat?.enqStartedAt)
      ) {
        dispatch(updateUserStatus({ ...chat }));
      } else {
        //skip
      }
    });

    if (Object.keys(user).length == 0 && chats.length != 0) {
      dispatch(changeUser(chats[0]));
    }
  }, [chats]);

  useEffect(() => {
    return () => {
      dispatch(resetChatUser());
    };
  }, []);
  useEffect(() => {
    return () => {
      dispatch(changeUser({}));
    };
  }, [agentChatStatus]);

  const loadMoreCustomersChat = async () => {
    if (!lastMessage) return;

    let q = query(
      getQueryParams(
        agentSearchItem,
        ticketStatusFilter,
        tagFilter,
        dateTimeRangeFilter,
        25,
        botNumber,
        agentChatStatus,
        allotedAgent,
        selectedAgent,
        lastMessage,
      ),
    );
    setLoading(true);
    const querySnapshot = await getDocs(q);
    setLoading(false);
    if (querySnapshot.docs.length === 0) {
      setLastMessage(null);
      return;
    }
    const groups = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      let conversationBetween = doc.id.split('-');
      let bot = conversationBetween[0];
      if (bot == allotedAgent.mob) {
        let lastMessage = '';
        if (data?.lastMessage) {
          try {
            lastMessage = formatMessage(JSON.parse(data?.lastMessage), data.messageType, true);
          } catch (error) {
            console.log(error);
          }
        }
        const conversation = {
          ...data,
          lastMessage: lastMessage,
          enqStartedAt: data?.enquiryStartTime,
        };

        groups.push(conversation);
      }
    });
    setChats((prev) => [...prev, ...groups]);
    if (groups.length > 0) {
      setLastMessage(querySnapshot.docs[querySnapshot.docs.length - 1]);
    }
  };

  const handleScroll = () => {
    if (customerContainerRef.current && !loading) {
      const { scrollTop, clientHeight, scrollHeight } = customerContainerRef.current;

      const tolerance = 1; // to handle decimal values of scrollTop

      if (scrollTop + clientHeight + tolerance >= scrollHeight) {
        const previousScrollTop = customerContainerRef.current.scrollTop;
        setLoading(true);

        loadMoreCustomersChat()
          .then(() => {
            setLoading(false);
            customerContainerRef.current.scrollTop = previousScrollTop;
          })
          .catch((error) => {
            setLoading(false);
            console.error(error);
          });
      }
    }
  };
  return (
    <div ref={customerContainerRef} onScroll={handleScroll} className="chats flex-grow-1">
      {loading && loadingSpinner()}
      {chats?.length !== 0 ? (
        chats.map((chat, i) => {
          const isUserSelected = user?.phone == chat?.phone;
          return (
            <div
              className={`userChat ${isUserSelected && 'userChatSeleceted'} border-left-${chat?.agentChatStatus}`}
              key={i}
              onClick={() => handleSelect(chat)}
            >
              <CustomerChatInfo customer={chat} />
            </div>
          );
        })
      ) : (
        <div className="h-full flex flex-column align-items-center justify-content-center">
          <span> No customers found</span>
        </div>
      )}
    </div>
  );
};

export default Chats;
